import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Sidebar from "./Sidebar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useActiveRole } from "../utils/activeRole";
import { AuthContext } from "../AuthProvider";
import { UserMenu } from "../components/nav/UserMenu";
import useBackendStatus from "../utils/useBackendStatus";
import Modal from "../components/Modal";
import { useTranslation } from "react-i18next";
import http from "../api/apiClient";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import { FaBell, FaTimes } from "react-icons/fa";
import { analyticsIdsFromPathname } from "../analytics/analyticsIds";
import { startIssuesEventSource } from "../utils/issuesSse";
import { getNoctisReachability } from "../api/noctisReachability";
import { useCan } from "../authz/useCan";
import { usePermissionsCtx } from "../authz/PermissionsProvider";
import ActivityPanel from "../components/ActivityPanel";
import { onPrintPauseStreams } from "../utils/printPage";

const ADMIN_PREFIXES = ["/admin", "/dashboard", "/school", "/reports"];
const ACTIVITY_PANEL_HIDDEN_KEY = "lumen:development:activityPanelHidden";

const readActivityPanelHidden = () => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage?.getItem(ACTIVITY_PANEL_HIDDEN_KEY) === "true";
  } catch {
    return false;
  }
};

const Layout = ({ darkMode }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { activeRole, setActiveRole } = useActiveRole();
  const { kcReady, keycloak, authenticated } = useContext(AuthContext);
  const { t } = useTranslation();
  const backendOnline = useBackendStatus();
  const { ready: permissionsReady } = usePermissionsCtx();
  const canSeeNoctis = useCan("integration.noctis.object-types", "view");
  const canViewActivities = useCan("development.activities", "read");
  const [fbOpen, setFbOpen] = useState(false);
  const [fbTitle, setFbTitle] = useState("");
  const [fbBody, setFbBody] = useState("");
  const [fbScreenshots, setFbScreenshots] = useState([]);
  const [fbShotTried, setFbShotTried] = useState(false);
  const [fbShotError, setFbShotError] = useState(null);
  const [fbLoading, setFbLoading] = useState(false);
  const [fbMsg, setFbMsg] = useState(null);
  const [fbError, setFbError] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [activityPanelHidden, setActivityPanelHidden] = useState(readActivityPanelHidden);

  const activityPanelCanBeHidden = process.env.NODE_ENV === "development";
  const canRenderActivityPanel = process.env.NODE_ENV === "development" && permissionsReady && canViewActivities;

  const setActivityPanelHiddenPersisted = useCallback((hidden) => {
    setActivityPanelHidden(hidden);
    if (typeof window === "undefined") return;
    try {
      window.localStorage?.setItem(ACTIVITY_PANEL_HIDDEN_KEY, hidden ? "true" : "false");
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    document.body.classList.add("admin-shell");
    document.documentElement.classList.add("admin-shell");
    return () => {
      document.body.classList.remove("admin-shell");
      document.documentElement.classList.remove("admin-shell");
    };
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("admin-shell-dark");
      document.documentElement.classList.add("admin-shell-dark");
    } else {
      document.body.classList.remove("admin-shell-dark");
      document.documentElement.classList.remove("admin-shell-dark");
    }
    return () => {
      document.body.classList.remove("admin-shell-dark");
      document.documentElement.classList.remove("admin-shell-dark");
    };
  }, [darkMode]);

  useEffect(() => {
    const isAdminRoute = ADMIN_PREFIXES.some((p) =>
      location.pathname.startsWith(p)
    );
    if (isAdminRoute && activeRole !== "admin") {
      setActiveRole("admin");
    }
  }, [location.pathname, activeRole, setActiveRole]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const ids = analyticsIdsFromPathname(location.pathname);
      window.__LUMEN_PAGE_ID__ = ids?.page || null;
      window.__LUMEN_FEATURE_ID__ = ids?.feature || null;
    } catch {
      // ignore
    }
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.addEventListener !== "function") return undefined;

    const handler = (e) => {
      const detail = e?.detail || {};
      let title = "";
      let message = "";
      let level = "";
      const sticky = Boolean(detail?.sticky);
      const href = typeof detail?.href === "string" && detail.href.trim() ? detail.href.trim() : null;
      const actionLabel =
        typeof detail?.actionLabel === "string" && detail.actionLabel.trim()
          ? detail.actionLabel.trim()
          : null;

      if (detail?.kind === "dry-run") {
        const ev = detail?.event || {};
        title = String(ev?.title || ev?.type || "Dry-run").trim() || "Dry-run";
        message =
          String(ev?.message || ev?.summary || ev?.description || ev?.detail || "").trim() ||
          (() => {
            try {
              return JSON.stringify(ev);
            } catch {
              return String(ev);
            }
          })();
        level = String(ev?.level || ev?.severity || "").trim().toLowerCase();
      } else {
        title = String(detail?.title || detail?.summary || detail?.kind || "Notificatie").trim() || "Notificatie";
        message =
          String(detail?.message || detail?.detail || detail?.description || "").trim() ||
          (() => {
            try {
              return JSON.stringify(detail);
            } catch {
              return String(detail);
            }
          })();
        level = String(detail?.level || detail?.severity || "").trim().toLowerCase();
      }

      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const toast = { id, title, message, level, href, actionLabel };

      setToasts((prev) => {
        const key = `${title}::${message}`;
        const already = prev.some((t) => `${t.title}::${t.message}` === key);
        if (already) return prev;
        return [...prev, { ...toast, sticky }].slice(-6);
      });

      if (!sticky) {
        window.setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 7000);
      }
    };

    window.addEventListener("lumen:toast", handler);
    return () => window.removeEventListener("lumen:toast", handler);
  }, []);

  useEffect(() => {
    if (!kcReady) return undefined;
    if (!authenticated) return undefined;
    if (typeof window === "undefined") return undefined;

    const shouldShowSseWarning = (() => {
      if (process.env.NODE_ENV === "development") return true;
      try {
        const host = window.location?.hostname?.toLowerCase?.() || "";
        return host === "localhost" || host === "127.0.0.1" || host === "::1";
      } catch {
        return false;
      }
    })();

    let active = true;
    let retryTimer = null;
    let stream = null;
    let retryMs = 1000;
    let warnedAuth = false;

    const lastEventStorageKey = (() => {
      const sub = keycloak?.tokenParsed?.sub || keycloak?.idTokenParsed?.sub || "anon";
      return `lumen:sse:lastEventId:${sub}`;
    })();

    const readLastEventId = () => {
      try {
        return window.localStorage?.getItem(lastEventStorageKey) || null;
      } catch {
        return null;
      }
    };

    const writeLastEventId = (value) => {
      try {
        if (!value) return;
        window.localStorage?.setItem(lastEventStorageKey, String(value));
      } catch {
        // ignore
      }
    };

    const emitToast = (detail) => {
      try {
        if (!active) return;
        window.dispatchEvent(new CustomEvent("lumen:toast", { detail }));
      } catch {
        // ignore
      }
    };

    const scheduleRetry = () => {
      if (!active) return;
      if (retryTimer) return;
      const next = Math.min(retryMs, 30000);
      retryMs = Math.min(retryMs * 2, 30000);
      retryTimer = window.setTimeout(() => {
        if (!active) return;
        retryTimer = null;
        connect();
      }, next);
    };

    const connect = () => {
      try {
        stream?.close?.();
      } catch {
        // ignore
      }
      stream = startIssuesEventSource({
        preferTicket: true,
        withCredentials: true,
        lastEventId: readLastEventId(),
        onLastEventId: writeLastEventId,
        onToast: (t) => emitToast(t),
        onStatus: (s) => {
          if (!active) return;
          if (s?.state === "open") {
            retryMs = 1000;
            warnedAuth = false;
            if (retryTimer) {
              window.clearTimeout(retryTimer);
              retryTimer = null;
            }
          }
          if (s?.state === "error") {
            if (shouldShowSseWarning && !warnedAuth) {
              warnedAuth = true;
              emitToast({
                title: "Live updates niet verbonden",
                message:
                  "Kan geen SSE-verbinding maken. Let op: EventSource kan geen Bearer header sturen; gebruik cookie-auth of een SSE ticket/nonce endpoint.",
                level: "warning",
              });
            }
            scheduleRetry();
          }
        },
      });
    };

    connect();

    // Safari only opens the print dialog once no connection is loading: close the live stream while printing.
    const stopPrintPause = onPrintPauseStreams({
      pause: () => {
        if (retryTimer) {
          window.clearTimeout(retryTimer);
          retryTimer = null;
        }
        stream?.close?.();
        stream = null;
      },
      resume: () => {
        if (active) connect();
      },
    });

    return () => {
      active = false;
      stopPrintPause();
      if (retryTimer) window.clearTimeout(retryTimer);
      try {
        stream?.close?.();
      } catch {
        // ignore
      }
    };
  }, [kcReady, authenticated, keycloak]);

  useEffect(() => {
    if (!kcReady) return undefined;
    if (!authenticated) return undefined;
    if (!canSeeNoctis) return undefined;
    if (typeof window === "undefined") return undefined;

    let active = true;
    let timer = null;
    let lastFailureAtNotified = null;
    let previouslyOk = true;

    try {
      lastFailureAtNotified = window.localStorage?.getItem("lumen:noctis:lastFailureAtNotified") || null;
    } catch {
      // ignore
    }

    const emitToast = (detail) => {
      try {
        if (!active) return;
        window.dispatchEvent(new CustomEvent("lumen:toast", { detail }));
      } catch {
        // ignore
      }
    };

    const persistNotified = (value) => {
      try {
        if (!value) return;
        window.localStorage?.setItem("lumen:noctis:lastFailureAtNotified", String(value));
      } catch {
        // ignore
      }
    };

    const poll = async () => {
      try {
        const s = await getNoctisReachability();
        if (!active) return;

        const enabled = s?.enabled ?? s?.configured ?? true;
        const reachable = Boolean(s?.reachable);
        const authorized = s?.authorized;
        const httpStatus = s?.httpStatus ?? s?.http_status ?? null;

        if (!enabled) {
          previouslyOk = true;
          return;
        }

        const ok = reachable && (authorized !== false);

        if (!ok) {
          const signature = String(httpStatus ?? "no-http");
          if (signature !== lastFailureAtNotified) {
            lastFailureAtNotified = signature;
            persistNotified(signature);
            previouslyOk = false;

            const msgParts = [
              reachable ? null : "Noctis is niet bereikbaar.",
              reachable && authorized === false ? "Noctis auth faalt (401/403)." : null,
              httpStatus != null ? `HTTP status: ${httpStatus}` : null,
            ].filter(Boolean);

            emitToast({
              title: "Noctis storing",
              message: msgParts.join("\n") || "Noctis communicatie faalt.",
              level: "warning",
              sticky: true,
              href: "/noctis/events",
              actionLabel: "Open Noctis logs",
            });
          }
        } else if (previouslyOk === false) {
          previouslyOk = true;
          emitToast({
            title: "Noctis hersteld",
            message: "Noctis is weer bereikbaar.",
            level: "success",
          });
        }
      } catch {
        // ignore: don't toast on status endpoint issues
      } finally {
        if (!active) return;
        timer = window.setTimeout(poll, 60000);
      }
    };

    timer = window.setTimeout(poll, 5000);
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [kcReady, authenticated, canSeeNoctis]);

  const requesterName = useMemo(() => {
    const parsed = keycloak?.idTokenParsed || keycloak?.tokenParsed || {};
    return (
      [parsed.given_name, parsed.family_name].filter(Boolean).join(" ") ||
      parsed.name ||
      parsed.preferred_username ||
      ""
    );
  }, [keycloak]);

  const closeSidebar = () => setSidebarOpen(false);
  const toggleSidebarCollapse = () =>
    setSidebarCollapsed((prev) => !prev);

  const loadHtml2Canvas = useCallback(
    () =>
      new Promise((resolve, reject) => {
        if (typeof window === "undefined") {
          reject(new Error("no-window"));
          return;
        }
        if (window.html2canvas) {
          resolve(window.html2canvas);
          return;
        }
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js";
        script.async = true;
        script.onload = () => resolve(window.html2canvas);
        script.onerror = reject;
        document.body.appendChild(script);
      }),
    []
  );

  const captureScreenshot = useCallback(async () => {
    setFbShotTried(true);
    try {
      const html2canvas = await loadHtml2Canvas();
      if (!html2canvas) throw new Error("missing-html2canvas");
      const scale = Math.min(typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1, 2);
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale,
        backgroundColor: "#ffffff",
        logging: false,
      });
      const dataUrl = canvas?.toDataURL("image/png");
      if (!dataUrl) return null;
      const estimatedBytes = Math.round((dataUrl.length * 3) / 4);
      const maxBytes = 5 * 1024 * 1024;
      if (estimatedBytes > maxBytes) {
        return null;
      }
      return { base64: dataUrl, name: "default-screenshot.png", size: estimatedBytes };
    } catch (err) {
      console.warn("Auto-screenshot failed", err);
      return null;
    }
  }, [loadHtml2Canvas]);

  const openFeedback = useCallback(async () => {
    setFbMsg(null);
    setFbError(null);
    setFbLoading(false);
    setFbShotTried(false);
    setFbShotError(null);
    setFbScreenshots([]);
    if (!fbShotTried) {
      const shot = await captureScreenshot();
      if (shot) {
        setFbScreenshots([shot]);
      } else {
        setFbShotError(
          t("dashboard.report_screenshot_auto_failed", {
            defaultValue:
              "Automatisch screenshot maken lukt niet in deze browser (Safari blokkeert dit soms). Upload eventueel handmatig een screenshot.",
          })
        );
      }
    }
    setFbOpen(true);
  }, [captureScreenshot, fbShotTried, t]);

  const closeFeedback = () => {
    setFbOpen(false);
    setFbLoading(false);
    setFbShotTried(false);
    setFbShotError(null);
    setFbMsg(null);
    setFbError(null);
  };

  const submitFeedback = async () => {
    const trimmedTitle = fbTitle?.trim() || "";
    const trimmedBody = fbBody?.trim() || "";
    if (!trimmedTitle || !trimmedBody) {
      setFbError(
        t("dashboard.report_required", {
          defaultValue: "Titel en omschrijving zijn verplicht.",
        })
      );
      return;
    }
    setFbLoading(true);
    setFbError(null);
    setFbMsg(null);
    try {
      const payload = {
        title: trimmedTitle,
        description: trimmedBody,
        pageUrl: typeof window !== "undefined" ? window.location.href : "",
        userAgent: typeof window !== "undefined" ? window.navigator.userAgent : "",
        requesterName: requesterName || undefined,
      };
      if (fbScreenshots.length > 0) {
        payload.screenshotsBase64 = fbScreenshots.map((a) => a.base64);
      }
      const { data } = await http.post("/issues", payload);
      const id = data?.number || data?.id || data?.issueNumber || data?.issue_id;
      const msg = id
        ? t("dashboard.report_sent_with_id", {
            defaultValue: "Melding is aangemaakt (#{{id}}).",
            id,
          })
        : t("dashboard.report_sent", { defaultValue: "Melding is aangemaakt." });
      setFbMsg(msg);
      setFbTitle("");
      setFbBody("");
      setFbScreenshots([]);
    } catch (e) {
      const detail =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.response?.data?.title ||
        (typeof e?.message === "string" ? e.message : null);
      setFbError(
        mapHttpErrorToMessage(e, t) ||
          detail ||
          t("dashboard.report_failed", { defaultValue: "Verzenden mislukt." })
      );
    } finally {
      setFbLoading(false);
    }
  };

  if (!kcReady) return null;

  const toastToneClasses = (level) => {
    if (level === "error" || level === "danger")
      return "border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-700/40 dark:bg-rose-900/30 dark:text-rose-100";
    if (level === "warn" || level === "warning")
      return "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/30 dark:text-amber-100";
    if (level === "success")
      return "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-700/40 dark:bg-emerald-900/30 dark:text-emerald-100";
    return "border-indigo-200 bg-white text-slate-900 dark:border-indigo-500/30 dark:bg-slate-900/90 dark:text-slate-100";
  };

  return (
    <>
    <div
      className={`${darkMode ? "dark" : ""} min-h-screen font-sans transition`}
    >
        <div className="flex min-h-screen">
          {/* Mobile sidebar overlay */}
          <div
          className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 print:hidden lg:hidden ${
            sidebarOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
          onClick={closeSidebar}
        />

        {/* Sidebar container */}
        <div
          className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 print:hidden lg:static lg:translate-x-0 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } lg:block`}
        >
          <div className="relative h-full">
            <div className="absolute inset-y-0 right-0 w-16 pointer-events-none bg-gradient-to-r from-indigo-900/30 via-indigo-900/10 to-transparent blur-2xl" />
            <div
              className={`min-h-screen lg:min-h-full transition-[width] duration-300 ${
                sidebarCollapsed ? "w-20" : "w-64"
              }`}
            >
                <Sidebar
                  collapsed={sidebarCollapsed}
                  onToggleCollapse={toggleSidebarCollapse}
                  onNavigate={closeSidebar}
                  backendOnline={backendOnline}
                  onReportIssue={openFeedback}
                />
              </div>
            </div>
          </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col p-4 transition bg-gradient-to-br from-slate-100 via-white to-emerald-50/40 print:bg-white print:p-0 sm:p-6 lg:p-10 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
          {/* Mobiele topbar met hamburger + UserMenu */}
          <div className="mb-4 flex items-center justify-between print:hidden lg:hidden">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800"
              aria-label="Open navigation"
            >
              <span className="block w-5 h-0.5 bg-gray-800 dark:bg-gray-100 mb-1" />
              <span className="block w-5 h-0.5 bg-gray-800 dark:bg-gray-100 mb-1" />
              <span className="block w-5 h-0.5 bg-gray-800 dark:bg-gray-100" />
            </button>

            <UserMenu
              variant="admin"
              backendOnline={backendOnline}
              buttonClassName="items-center gap-2"
              textClassName="text-gray-800 dark:text-gray-100"
              menuItemsClassName="bg-white/95 text-gray-900 border border-gray-200 shadow-xl rounded-xl overflow-hidden dark:bg-gray-900/95 dark:text-gray-100 dark:border-gray-700"
              itemsWrapperClassName="mt-3 right-0 w-56"
              onReportIssue={openFeedback}
            />
          </div>

          {/* Eigenlijke pagina-inhoud */}
          <div className="flex-1 min-w-0">
            <Outlet />
          </div>
        </div>
        {canRenderActivityPanel && (!activityPanelCanBeHidden || !activityPanelHidden) && (
          <div className="print:hidden">
            <ActivityPanel
              onHide={
                activityPanelCanBeHidden
                  ? () => setActivityPanelHiddenPersisted(true)
                  : undefined
              }
            />
          </div>
        )}
      </div>
    </div>

    {canRenderActivityPanel && activityPanelCanBeHidden && activityPanelHidden && (
      <button
        type="button"
        onClick={() => setActivityPanelHiddenPersisted(false)}
        className="fixed bottom-4 right-4 z-[10000] inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-white/95 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-xl backdrop-blur transition hover:-translate-y-0.5 hover:border-indigo-300 hover:bg-indigo-50 print:hidden dark:border-indigo-500/40 dark:bg-slate-900/95 dark:text-indigo-200 dark:hover:bg-slate-800"
      >
        <FaBell className="h-3.5 w-3.5" />
        {t("activitySidebar.show", { defaultValue: "Toon activiteit" })}
      </button>
    )}

    {toasts.length > 0 && (
      <div className="pointer-events-none fixed right-4 top-4 z-[10000] flex w-full max-w-sm flex-col gap-3 print:hidden">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`pointer-events-auto rounded-2xl border p-4 shadow-xl ${toastToneClasses(toast.level)}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">{toast.title}</div>
                <div className="mt-1 whitespace-pre-wrap text-sm opacity-90">{toast.message}</div>
                {toast.href && (
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        navigate(toast.href);
                      } finally {
                        setToasts((prev) => prev.filter((t) => t.id !== toast.id));
                      }
                    }}
                    className="mt-3 inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200"
                  >
                    {toast.actionLabel || t("common.open", { defaultValue: "Open" })}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
                className="rounded-lg p-1 opacity-70 hover:opacity-100"
                aria-label={t("common.close", { defaultValue: "Close" })}
              >
                <FaTimes />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}

    <Modal
      show={fbOpen}
      title={t("dashboard.report_issue", { defaultValue: "Report an issue" })}
      type="info"
      onClose={closeFeedback}
      hideDefaultActions
    >
      <div className="space-y-3 text-left">
        {fbMsg ? (
          <div className="space-y-3">
            <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800 dark:border-green-900 dark:bg-green-900/30 dark:text-green-100">
              {fbMsg}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={closeFeedback}
                className="rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200"
              >
                {t("common.actions.close", { defaultValue: "Close" })}
              </button>
            </div>
          </div>
        ) : (
          <>
            {fbError && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/30 dark:text-red-100">
                {fbError?.message || fbError?.title || fbError}
              </div>
            )}
            <div>
              <label className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {t("dashboard.report_title", { defaultValue: "Title" })}
              </label>
              <input
                value={fbTitle}
                onChange={(e) => setFbTitle(e.target.value)}
                required
                className="mt-1 w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                placeholder={t("dashboard.report_title_ph", { defaultValue: "Short summary" })}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {t("dashboard.report_body", { defaultValue: "What happened?" })}
              </label>
              <textarea
                value={fbBody}
                onChange={(e) => setFbBody(e.target.value)}
                rows={4}
                required
                className="mt-1 w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                placeholder={t("dashboard.report_body_ph", { defaultValue: "Steps to reproduce, expected vs. actual" })}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {t("dashboard.report_screenshot", { defaultValue: "Screenshots (optional)" })}
              </label>
              {fbShotError && fbScreenshots.length === 0 && (
                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/60 dark:bg-amber-900/30 dark:text-amber-100">
                  {fbShotError}
                </div>
              )}
              <input
                type="file"
                multiple
                accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="mt-1 block w-full text-sm text-gray-700 dark:text-gray-200"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (!files.length) return;
                  const readers = files.map(
                    (file) =>
                      new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = () => {
                          const result = typeof reader.result === "string" ? reader.result : null;
                          resolve({ base64: result, name: file.name, size: file.size });
                        };
                        reader.readAsDataURL(file);
                      })
                  );
                  Promise.all(readers).then((arr) =>
                    setFbScreenshots((prev) => [...prev, ...arr.filter((a) => a.base64)])
                  );
                }}
              />
              {fbScreenshots.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
                  {fbScreenshots.map((a, idx) => (
                    <li
                      key={`${a.name || "img"}-${idx}`}
                      className="flex items-center justify-between rounded border border-gray-200 bg-gray-50 px-2 py-1 gap-2 dark:border-gray-700 dark:bg-gray-800"
                    >
                      <span className="truncate">{a.name || t("common.image", { defaultValue: "Image" })}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">
                          {Math.round((a.size || 0) / 1024)} KB
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setFbScreenshots((prev) =>
                              prev.filter((_, i) => i !== idx)
                            )
                          }
                          className="rounded-full p-1 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                          aria-label={t("common.remove", { defaultValue: "Remove" })}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </li>
                  ))}
                  <li className="text-[11px] text-gray-500 dark:text-gray-400">
                    {t("dashboard.report_screenshot_added", {
                      defaultValue: "Allowed: images, PDF/Word, max 5 MB each. You can add more by selecting again.",
                    })}
                  </li>
                </ul>
              )}
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/60 dark:bg-amber-900/30 dark:text-amber-100">
              {t("dashboard.report_hint", {
                defaultValue: "We include the current page and your browser info to help debugging.",
              })}
            </div>
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closeFeedback}
                disabled={fbLoading}
                className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:text-gray-900 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={submitFeedback}
                disabled={fbLoading}
                className="rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 disabled:opacity-50 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200"
              >
                {fbLoading ? t("common.loading", { defaultValue: "Loading…" }) : t("common.send", { defaultValue: "Send" })}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>

    </>
  );
};

export default Layout;
